import { levelRange } from './resourceHunterUtils';

type ZoneMetadataLineProps = {
  expansion: string | null;
  minLevel: number | null;
  maxLevel: number | null;
  zoneType: string | null;
  className?: string;
  fallbackText?: string;
};

const LEVEL_COLOR_RULES: Array<{ maxExclusive: number; color: string }> = [
  { maxExclusive: 10, color: '#9ba6af' },
  { maxExclusive: 20, color: '#22c076' },
  { maxExclusive: 30, color: '#0dcaf0' },
  { maxExclusive: 40, color: '#3c8aff' },
  { maxExclusive: 50, color: '#a3c8f8' },
  { maxExclusive: 60, color: '#ffc107' },
  { maxExclusive: 70, color: '#fd7e14' },
  { maxExclusive: 80, color: '#fd3714' }
];

const EXPANSION_COLORS: Record<string, string> = {
  'Classic': '#e2e5e8',
  'Ruins of Kunark': '#9c6547',
  'Scars of Velious': '#1f8ef1',
  'Shadows of Luclin': '#b05cc7',
  'Planes of Power': '#e8a600',
  'Lost Dungeons of Norrath': '#1794a3',
  'Legacy of Ykesha': '#d2652a',
  'Gates of Discord': '#c62828',
  'Omens of War': '#618173'
};

const ZONE_TYPE_COLORS: Record<string, string> = {
  Indoor: '#986c4c',
  Outdoor: '#26c021'
};

function averageLevel(minLevel: number | null, maxLevel: number | null) {
  if (minLevel == null && maxLevel == null) return null;
  if (minLevel != null && maxLevel != null) return (minLevel + maxLevel) / 2;
  return minLevel ?? maxLevel;
}

function levelColor(minLevel: number | null, maxLevel: number | null) {
  const avg = averageLevel(minLevel, maxLevel);
  if (avg == null) return null;

  const matched = LEVEL_COLOR_RULES.find((rule) => avg < rule.maxExclusive);
  return matched?.color ?? '#dc3545';
}

export function ZoneMetadataLine({ expansion, minLevel, maxLevel, zoneType, className, fallbackText = 'No zone metadata' }: ZoneMetadataLineProps) {
  const levelText = levelRange(minLevel, maxLevel);
  const levelHex = levelColor(minLevel, maxLevel);
  const expansionHex = expansion ? EXPANSION_COLORS[expansion] : null;
  const zoneTypeHex = zoneType ? ZONE_TYPE_COLORS[zoneType] : null;

  if (!expansion && !levelText && !zoneType) {
    return <>{fallbackText}</>;
  }

  return (
    <span className={className}>
      {expansion ? <span style={expansionHex ? { color: expansionHex } : undefined}>{expansion}</span> : null}
      {expansion && (levelText || zoneType) ? ' · ' : null}
      {levelText ? <span style={levelHex ? { color: levelHex } : undefined}>{levelText}</span> : null}
      {levelText && zoneType ? ' · ' : null}
      {zoneType ? <span style={zoneTypeHex ? { color: zoneTypeHex } : undefined}>{zoneType}</span> : null}
    </span>
  );
}
